import {
  ComboboxItem,
  Button,
  Group,
  Loader,
  Autocomplete as MantineAutocomplete,
  Checkbox as MantineCheckbox,
  MultiSelect as MantineMultiSelect,
  Select as MantineSelect,
  SegmentedControl as MantineSegmentedControl,
  Textarea as MantineTextarea,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Tooltip,
  Card,
  Title,
  Progress,
  rem,
  Box,
  ThemeIcon,
  Skeleton,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { DateTimePicker } from "@mantine/dates";
import { localDateTimeToISO } from "../../../../utils/datetime";
import { useForm, yupResolver } from "@mantine/form";
import { GetInputProps } from "@mantine/form/lib/types";
import { useDebouncedValue } from "@mantine/hooks";
import { Link, RichTextEditor } from "@mantine/tiptap";
import { Editor } from "@monaco-editor/react";
import {
  IconInfoCircle,
  IconCheck,
  IconAlertCircle,
} from "@tabler/icons-react";
import Highlight from "@tiptap/extension-highlight";
import SubScript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  FC,
  Fragment,
  ReactElement,
  useEffect,
  useState,
  useMemo,
} from "react";
import * as yup from "yup";
import { AnySchema, ObjectShape } from "yup";

const useStyles = createStyles((theme) => ({
  formCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease",
    maxWidth: rem(800),
    width: "100%",
    margin: "0 auto",
  },
  formTitle: {
    fontSize: rem(24),
    fontWeight: 700,
    color: theme.colors.dark[0],
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  fieldWrapper: {
    marginBottom: theme.spacing.md,
    transition: "all 200ms ease",
  },
  fieldError: {},
  progressBar: {
    marginBottom: theme.spacing.lg,
  },
  progressLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
    marginBottom: theme.spacing.xs,
  },
  submitButton: {
    marginTop: theme.spacing.xl,
    fontWeight: 600,
    letterSpacing: "0.025em",
  },
  subtext: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
    marginTop: rem(4),
    fontStyle: "italic",
  },
  errorText: {
    color: theme.colors.rose[5],
    fontSize: theme.fontSizes.sm,
    marginTop: rem(4),
    display: "flex",
    alignItems: "center",
    gap: rem(4),
  },
  successIndicator: {
    color: theme.colors.green[5],
    marginLeft: rem(8),
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.xl,
    minHeight: "100%",
    width: "100%",
  },
  skeletonField: {
    marginBottom: theme.spacing.md,
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    flex: 1,
  },
  previewToolbar: {
    padding: `${rem(8)} ${rem(12)}`,
    borderBottom: `1px solid ${theme.colors.dark[5]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  previewIframeWrapper: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing.md,
    backgroundColor: theme.colors.dark[8],
    overflow: "auto",
  },
  previewIframe: {
    border: "none",
    backgroundColor: "#f4f5f6",
    borderRadius: theme.radius.md,
    width: "100%",
    height: "100%",
    minHeight: rem(400),
  },
  previewSizeLabel: {
    padding: `${rem(4)} ${rem(12)}`,
    borderTop: `1px solid ${theme.colors.dark[5]}`,
    textAlign: "center" as const,
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
  layoutContainer: {
    display: "flex",
    height: "calc(100vh - 120px)",
    overflow: "hidden",
  },
  layoutContainerColumn: {
    flexDirection: "column" as const,
  },
  layoutRegion: {
    display: "flex",
    flexDirection: "column" as const,
    overflow: "auto",
    padding: theme.spacing.md,
    borderRight: `1px solid ${theme.colors.dark[5]}`,
    "&:last-child": {
      borderRight: "none",
    },
  },
  layoutRegionHeader: {
    fontSize: rem(11),
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    color: theme.colors.cyan[4],
    fontWeight: 600,
    marginBottom: theme.spacing.md,
  },
  topToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${rem(12)} ${theme.spacing.md}`,
    borderBottom: `1px solid ${theme.colors.dark[5]}`,
  },
  formCardLayout: {
    maxWidth: "100%",
    margin: 0,
    borderRadius: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
  },
}));

interface LayoutRegion {
  key: string;
  label?: string;
  flex?: number;
  width?: string;
  fields: string[];
}

interface FormLayout {
  regions: LayoutRegion[];
  direction?: "row" | "column";
  toolbar?: "top" | "bottom" | "none";
}

interface BaseFormComponent {
  yupConfig: AnySchema;
  label: string;
  info?: string | ReactElement;
  subtext?: string | ReactElement;
  onChange?: (value: string) => void;
  dynamicSubtext?: (value: string) => string;
  defaultValue?: any;
  disabled?: boolean;
  value?: any;
}

interface DynamicConfig extends BaseFormComponent {
  type: "DYNAMIC";
  element: (onChange: (...newValues: any[]) => void) => ReactElement;
}

interface TextareaConfig extends BaseFormComponent {
  type: "TEXTAREA";
  minRows?: number;
  maxRows?: number;
}

interface RichTextEditorConfig extends Omit<BaseFormComponent, "value"> {
  type: "RICH_TEXT_EDITOR";
}

interface CodeEditorConfig extends BaseFormComponent {
  type: "CODE_EDITOR";
  language?: string;
  height?: string;
}

interface PreviewPanelConfig extends BaseFormComponent {
  type: "PREVIEW_PANEL";
  watchField: string;
}

interface CheckboxConfig extends Omit<BaseFormComponent, "onChange"> {
  type: "CHECKBOX";
  onChange?: (value: boolean) => void;
}

interface InputConfig extends BaseFormComponent {
  type: "INPUT";
  inputType: "text" | "password" | "email" | "number" | "tel" | "url" | "date";
  placeholder?: string;
}

interface SelectConfig extends BaseFormComponent {
  type: "SELECT";
  options: { label: string; value: string }[];
  searchable?: boolean;
}

interface MultiSelectConfig extends Omit<SelectConfig, "onChange" | "type"> {
  type: "MULTI_SELECT";
  options: { label: string; value: string }[];
  onChange?: (value: string[]) => void;
}

interface AutocompleteConfig extends BaseFormComponent {
  type: "AUTOCOMPLETE";
  populate: (value: string) => Promise<ComboboxItem[]>;
  transformResult?: (result: any) => ComboboxItem;
  itemComponent?: FC<any>;
}

type Schema = Record<
  string,
  | InputConfig
  | TextareaConfig
  | SelectConfig
  | AutocompleteConfig
  | CheckboxConfig
  | DynamicConfig
  | RichTextEditorConfig
  | CodeEditorConfig
  | MultiSelectConfig
  | PreviewPanelConfig
>;

// Field wrapper with validation state
const FieldWrapper = ({
  children,
  error,
  touched,
  flex,
}: {
  children: React.ReactNode;
  error?: string;
  touched?: boolean;
  flex?: boolean;
}) => {
  const { classes, cx } = useStyles();

  return (
    <Box
      className={cx(
        classes.fieldWrapper,
        error && touched && classes.fieldError,
      )}
      style={
        flex ? { flex: 1, display: "flex", flexDirection: "column" } : undefined
      }
    >
      {children}
    </Box>
  );
};

const Dynamic = ({
  id,
  getInputProps,
  element,
}: DynamicConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const { classes } = useStyles();

  return (
    <FieldWrapper error={getInputProps(id).error}>
      <DynamicComponent toRender={element(getInputProps(id).onChange)} />
      {getInputProps(id).error && (
        <Text className={classes.errorText}>
          <IconAlertCircle size={14} />
          {getInputProps(id).error}
        </Text>
      )}
    </FieldWrapper>
  );
};

/* eslint-disable react/prop-types */
const Input = ({
  id,
  label,
  info,
  placeholder,
  defaultValue,
  getInputProps,
  inputType,
  disabled,
  value,
  onChange,
}: InputConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const { classes } = useStyles();
  const props = getInputProps(id);
  props.value = value ?? props.value;

  const rightSection = info ? (
    <Tooltip label={info} position="left" withArrow>
      <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
        <IconInfoCircle size={16} />
      </Text>
    </Tooltip>
  ) : props.value && !props.error ? (
    <ThemeIcon
      size="xs"
      radius="xl"
      color="green"
      variant="light"
      className={classes.successIndicator}
    >
      <IconCheck size={12} />
    </ThemeIcon>
  ) : null;

  if (inputType === "date") {
    return (
      <FieldWrapper error={props.error} touched={!!props.value}>
        <DateTimePicker
          clearable={true}
          label={label}
          rightSection={rightSection}
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          {...props}
          value={props.value || null}
          onChange={(value) => {
            const iso = localDateTimeToISO(value);
            props.onChange(iso);
            onChange?.(iso);
          }}
        />
      </FieldWrapper>
    );
  }

  if (inputType === "number") {
    return (
      <FieldWrapper error={props.error} touched={!!props.value}>
        <NumberInput
          disabled={disabled}
          label={label}
          id={id}
          rightSection={rightSection}
          defaultValue={defaultValue}
          placeholder={placeholder}
          {...props}
          value={props.value}
          onChange={(value) => {
            props.onChange(value);
            onChange?.(value?.toString() ?? "");
          }}
        />
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper error={props.error} touched={!!props.value}>
      <TextInput
        disabled={disabled}
        type={inputType}
        label={label}
        rightSection={rightSection}
        id={id}
        placeholder={placeholder}
        defaultValue={defaultValue}
        {...props}
        value={props.value}
        onChange={(event) => {
          props.onChange(event);
          onChange?.(event.target.value ?? "");
        }}
      />
    </FieldWrapper>
  );
};

const Textarea = ({
  id,
  label,
  info,
  defaultValue,
  getInputProps,
  disabled,
  value,
  minRows = 3,
  maxRows,
}: TextareaConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const props = getInputProps(id);

  return (
    <FieldWrapper error={props.error} touched={!!props.value}>
      <MantineTextarea
        disabled={disabled}
        label={label}
        rightSection={
          info && (
            <Tooltip label={info} position="left" withArrow>
              <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
                <IconInfoCircle size={16} />
              </Text>
            </Tooltip>
          )
        }
        id={id}
        defaultValue={defaultValue}
        minRows={minRows}
        maxRows={maxRows}
        autosize
        {...props}
        value={value ?? props.value}
      />
    </FieldWrapper>
  );
};

const Checkbox = ({
  id,
  label,
  info,
  defaultValue,
  getInputProps,
  disabled,
  value,
  onChange,
}: CheckboxConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const props = getInputProps(id);

  return (
    <FieldWrapper error={props.error}>
      <MantineCheckbox
        disabled={disabled}
        label={
          <Group gap={6}>
            <Text size="sm">{label}</Text>
            {info && (
              <Tooltip label={info} position="right" withArrow>
                <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
                  <IconInfoCircle size={14} />
                </Text>
              </Tooltip>
            )}
          </Group>
        }
        id={id}
        {...props}
        defaultChecked={defaultValue || false}
        defaultValue={defaultValue || false}
        value={value ?? props.value}
        onChange={(event) => {
          props.onChange(event);
          onChange?.(event.currentTarget.checked);
        }}
      />
    </FieldWrapper>
  );
};

const Select = ({
  id,
  label,
  options,
  info,
  defaultValue,
  onChange,
  getInputProps,
  disabled,
  value,
  searchable = true,
}: SelectConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const registration = getInputProps(id);
  useEffect(() => {
    if (defaultValue) {
      onChange?.(defaultValue);
      registration.onChange(defaultValue);
    }
  }, []);

  return (
    <FieldWrapper error={registration.error} touched={!!registration.value}>
      <MantineSelect
        clearable={true}
        searchable={searchable}
        disabled={disabled}
        label={label}
        rightSection={
          info && (
            <Tooltip label={info} position="left" withArrow>
              <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
                <IconInfoCircle size={16} />
              </Text>
            </Tooltip>
          )
        }
        id={id}
        {...registration}
        value={value ?? registration.value}
        data={options}
        defaultValue={defaultValue}
        onChange={(change) => {
          if (change) {
            onChange?.(change);
          }
          registration.onChange(change);
        }}
      />
    </FieldWrapper>
  );
};

const MultiSelect = ({
  id,
  label,
  options,
  info,
  defaultValue,
  onChange,
  getInputProps,
  disabled,
  value,
  searchable = true,
}: MultiSelectConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const registration = getInputProps(id);
  useEffect(() => {
    if (defaultValue) {
      onChange?.(defaultValue);
      registration.onChange(defaultValue);
    }
  }, []);

  return (
    <FieldWrapper
      error={registration.error}
      touched={!!registration.value?.length}
    >
      <MantineMultiSelect
        clearable={true}
        searchable={searchable}
        disabled={disabled}
        label={label}
        rightSection={
          info && (
            <Tooltip label={info} position="left" withArrow>
              <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
                <IconInfoCircle size={16} />
              </Text>
            </Tooltip>
          )
        }
        id={id}
        {...registration}
        value={value ?? registration.value}
        data={options}
        defaultValue={defaultValue}
        onChange={(change) => {
          if (change) {
            onChange?.(change);
          }
          registration.onChange(change);
        }}
      />
    </FieldWrapper>
  );
};

const Autocomplete = ({
  id,
  label,
  defaultValue,
  populate,
  info,
  getInputProps,
  itemComponent,
  transformResult,
  disabled,
  value: controlledValue,
}: AutocompleteConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
  itemComponent?: FC<any>;
}) => {
  const props = getInputProps(id);
  const [data, setData] = useState<ComboboxItem[]>([]);
  const [debouncedValue] = useDebouncedValue(props.value, 500, {
    leading: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (debouncedValue) {
      setLoading(true);
      populate(debouncedValue)
        .then(setData)
        .finally(() => setLoading(false));
    }
  }, [debouncedValue, populate]);

  return (
    <FieldWrapper error={props.error} touched={!!props.value}>
      <MantineAutocomplete
        disabled={disabled}
        label={
          <Group gap={6}>
            <Text size="sm">{label}</Text>
            {info && (
              <Tooltip label={info} position="right" withArrow>
                <Text size="xs" c="dimmed" style={{ cursor: "help" }}>
                  <IconInfoCircle size={14} />
                </Text>
              </Tooltip>
            )}
          </Group>
        }
        data={data}
        defaultValue={defaultValue}
        filter={({ options }: any) => options}
        rightSection={loading && <Loader size="xs" />}
        {...props}
        onOptionSubmit={(value: string) => {
          const item = data?.find(
            (d: any) => (typeof d === "string" ? d : d.value) === value,
          );
          props.onChange(
            transformResult ? transformResult(item ?? value) : (item ?? value),
          );
        }}
        value={controlledValue ?? props.value}
      />
    </FieldWrapper>
  );
};

const RichTextEditorElement = ({
  id,
  label,
  defaultValue,
  getInputProps,
  disabled,
}: RichTextEditorConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const { classes } = useStyles();
  const props = getInputProps(id);
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit,
      Underline,
      Link,
      Superscript,
      SubScript,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],

    content: defaultValue,
    onUpdate: ({ editor }) => {
      props.onChange(editor.getHTML());
    },
  });

  return (
    <FieldWrapper error={props.error}>
      <Text size="sm" fw={500} mb="xs">
        {label}
      </Text>
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar sticky stickyOffset={60}>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
            <RichTextEditor.Highlight />
            <RichTextEditor.Code />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
            <RichTextEditor.H4 />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Blockquote />
            <RichTextEditor.Hr />
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
            <RichTextEditor.Subscript />
            <RichTextEditor.Superscript />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignJustify />
            <RichTextEditor.AlignRight />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content />
      </RichTextEditor>

      {props.error && (
        <Text className={classes.errorText}>
          <IconAlertCircle size={14} />
          {props.error}
        </Text>
      )}
    </FieldWrapper>
  );
};

const CodeEditor = ({
  id,
  label,
  defaultValue,
  getInputProps,
  value,
  language,
  onChange,
  height = "400px",
}: CodeEditorConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
}) => {
  const { classes } = useStyles();
  const props = getInputProps(id);

  const fillHeight = height === "100%";

  return (
    <FieldWrapper error={props.error} flex={fillHeight}>
      <Text size="sm" fw={500} mb="xs">
        {label}
      </Text>
      <Box
        style={(theme) => ({
          border: `1px solid ${theme.colors.dark[4]}`,
          borderRadius: theme.radius.md,
          overflow: "hidden",
          ...(fillHeight && {
            flex: 1,
            display: "flex",
            flexDirection: "column" as const,
          }),
        })}
      >
        <Editor
          language={language}
          height={fillHeight ? "100%" : height}
          theme="vs-dark"
          defaultValue={defaultValue}
          value={value}
          options={{
            selectOnLineNumbers: true,
            minimap: { enabled: false },
            fontSize: 14,
            padding: { top: 16 },
            scrollBeyondLastLine: false,
          }}
          onChange={(value) => {
            props.onChange(value);
            onChange?.(value ?? "");
          }}
        />
      </Box>

      {props.error && (
        <Text className={classes.errorText}>
          <IconAlertCircle size={14} />
          {props.error}
        </Text>
      )}
    </FieldWrapper>
  );
};

type DeviceMode = "desktop" | "tablet" | "mobile";

const DEVICE_WIDTHS: Record<DeviceMode, string> = {
  desktop: "100%",
  tablet: "768px",
  mobile: "375px",
};

const DEVICE_LABELS: Record<DeviceMode, string> = {
  desktop: "Desktop",
  tablet: "Tablet — 768px",
  mobile: "Mobile — 375px",
};

const PreviewPanel = ({
  label,
  watchField,
  formValues,
}: PreviewPanelConfig & {
  id: string;
  getInputProps: GetInputProps<any>;
  formValues?: Record<string, unknown>;
}) => {
  const { classes } = useStyles();
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("desktop");
  const rawContent = (formValues?.[watchField] as string) ?? "";
  const [debouncedContent] = useDebouncedValue(rawContent, 300);

  return (
    <Box className={classes.previewContainer}>
      <Box className={classes.previewToolbar}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <MantineSegmentedControl
          size="xs"
          value={deviceMode}
          onChange={(value) => setDeviceMode(value as DeviceMode)}
          data={[
            { label: "Desktop", value: "desktop" },
            { label: "Tablet", value: "tablet" },
            { label: "Mobile", value: "mobile" },
          ]}
        />
      </Box>
      <Box className={classes.previewIframeWrapper}>
        <iframe
          className={classes.previewIframe}
          srcDoc={debouncedContent}
          sandbox=""
          title="Email preview"
          style={{
            maxWidth: DEVICE_WIDTHS[deviceMode],
            transition: "max-width 200ms ease",
          }}
        />
      </Box>
      <Box className={classes.previewSizeLabel}>
        {DEVICE_LABELS[deviceMode]}
      </Box>
    </Box>
  );
};

const COMPONENTS = {
  INPUT: Input,
  TEXTAREA: Textarea,
  SELECT: Select,
  MULTI_SELECT: MultiSelect,
  AUTOCOMPLETE: Autocomplete,
  CHECKBOX: Checkbox,
  DYNAMIC: Dynamic,
  RICH_TEXT_EDITOR: RichTextEditorElement,
  CODE_EDITOR: CodeEditor,
  PREVIEW_PANEL: PreviewPanel,
};

const DynamicComponent = ({ toRender }: { toRender: ReactElement }) => (
  <>{toRender}</>
);

// Form skeleton for loading state
const FormSkeleton = ({ fieldCount = 4 }: { fieldCount?: number }) => {
  const { classes } = useStyles();

  return (
    <Stack gap="md">
      {Array.from({ length: fieldCount }).map((_, i) => (
        <Box key={i} className={classes.skeletonField}>
          <Skeleton height={14} width="25%" mb="xs" radius="sm" />
          <Skeleton height={36} radius="md" />
        </Box>
      ))}
      <Skeleton height={36} width="30%" mt="md" radius="md" />
    </Stack>
  );
};

export interface CreationFormProps<T extends Schema> {
  title?: string;
  subtitle?: string;
  submitText?: string;
  schemaDefinition: T;
  onSubmit: (datas: Record<keyof T, any>) => Promise<void>;
  showProgress?: boolean;
  loading?: boolean;
  compact?: boolean;
  layout?: FormLayout;
}

export const CreationForm = <T extends Schema>({
  title,
  subtitle,
  submitText,
  schemaDefinition,
  onSubmit,
  showProgress = false,
  loading: externalLoading = false,
  compact = false,
  layout,
}: CreationFormProps<T>) => {
  const { classes, cx } = useStyles();
  const [submitting, setSubmitting] = useState(false);

  const schema = yup
    .object(
      Object.entries(schemaDefinition).reduce((acc, [key, value]) => {
        acc[key] = value.yupConfig;
        return acc;
      }, {} as ObjectShape),
    )
    .required();

  const {
    onSubmit: onSubmitForm,
    errors,
    getInputProps,
    values,
  } = useForm({
    validate: yupResolver(schema),
    initialValues: Object.entries(schemaDefinition).reduce(
      (acc, curr) => {
        acc[curr[0] as keyof T] = curr[1].defaultValue ?? "";
        return acc;
      },
      {} as Record<keyof T, string>,
    ),
  });

  // Calculate form progress
  const progress = useMemo(() => {
    if (!showProgress) return 0;
    const totalFields = Object.entries(schemaDefinition).filter(
      ([, v]) => v.type !== "PREVIEW_PANEL",
    ).length;
    const filledFields = Object.entries(values).filter(([key, val]) => {
      const config = schemaDefinition[key];
      if (!config) return false;
      if (config.type === "CHECKBOX") return Boolean(val);
      if (Array.isArray(val)) return val.length > 0;
      const strVal = val as unknown;
      return strVal !== "" && strVal !== null && strVal !== undefined;
    }).length;
    return Math.round((filledFields / totalFields) * 100);
  }, [values, schemaDefinition, showProgress]);

  // Compute dynamic subtexts immutably
  const dynamicSubtexts = useMemo(() => {
    const result: Record<string, string> = {};
    Object.entries(schemaDefinition).forEach(([key, value]) => {
      if (value.dynamicSubtext) {
        const updatedValue = value.dynamicSubtext(values[key] as any);
        if (typeof updatedValue === "string") {
          result[key] = updatedValue;
        }
      }
    });
    return result;
  }, [values, schemaDefinition]);

  const submit = (data: any) => {
    setSubmitting(true);
    const filteredData = { ...data };
    Object.entries(schemaDefinition).forEach(([key, value]) => {
      if (value.type === "PREVIEW_PANEL") {
        delete filteredData[key];
      }
    });
    onSubmit(filteredData).finally(() => setSubmitting(false));
  };

  if (layout) {
    const toolbarMode = layout.toolbar ?? "bottom";
    const direction = layout.direction ?? "row";

    const renderField = (key: string) => {
      const value = schemaDefinition[key];
      if (!value) return null;
      return (
        <Fragment key={key}>
          <DynamicComponent
            toRender={COMPONENTS[value.type]({
              ...value,
              id: key,
              getInputProps,
              errors,
              formValues: value.type === "PREVIEW_PANEL" ? values : undefined,
            } as any)}
          />
          {(dynamicSubtexts[key] || value.subtext) && (
            <Text className={classes.subtext}>
              {dynamicSubtexts[key] || value.subtext}
            </Text>
          )}
        </Fragment>
      );
    };

    return (
      <form
        onSubmit={onSubmitForm(submit)}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Card
          className={cx(classes.formCard, classes.formCardLayout)}
          p={0}
          radius={0}
        >
          {toolbarMode === "top" && (
            <Box className={classes.topToolbar}>
              <Box>
                {title && (
                  <Title order={4} className={classes.formTitle}>
                    {title}
                  </Title>
                )}
                {subtitle && (
                  <Text size="sm" c="dimmed">
                    {subtitle}
                  </Text>
                )}
              </Box>
              <Button
                className={classes.submitButton}
                loading={submitting}
                type="submit"
                size="sm"
              >
                {submitText || "Save"}
              </Button>
            </Box>
          )}

          {externalLoading ? (
            <FormSkeleton fieldCount={Object.keys(schemaDefinition).length} />
          ) : (
            <Box
              className={cx(
                classes.layoutContainer,
                direction === "column" && classes.layoutContainerColumn,
              )}
            >
              {layout.regions.map((region) => (
                <Box
                  key={region.key}
                  className={classes.layoutRegion}
                  style={{
                    flex: region.width ? undefined : (region.flex ?? 1),
                    width: region.width,
                    minWidth: region.width,
                  }}
                >
                  {region.label && (
                    <Text className={classes.layoutRegionHeader}>
                      {region.label}
                    </Text>
                  )}
                  <Stack gap="sm" style={{ flex: 1 }}>
                    {region.fields.map(renderField)}
                  </Stack>
                </Box>
              ))}
            </Box>
          )}

          {toolbarMode === "bottom" && (
            <Box p="md">
              <Button
                className={classes.submitButton}
                loading={submitting}
                type="submit"
                size={compact ? "sm" : "md"}
                fullWidth={compact}
              >
                {submitText || "Submit"}
              </Button>
            </Box>
          )}
        </Card>
      </form>
    );
  }

  return (
    <Box className={classes.formContainer}>
      <Card className={classes.formCard} p={compact ? "lg" : "xl"} radius="lg">
        {/* Header */}
        {title && (
          <Box mb="lg">
            <Title order={3} className={classes.formTitle}>
              {title}
            </Title>
            {subtitle && (
              <Text size="sm" c="dimmed" mt="xs">
                {subtitle}
              </Text>
            )}
          </Box>
        )}

        {/* Progress indicator */}
        {showProgress && !externalLoading && (
          <Box className={classes.progressBar}>
            <Text className={classes.progressLabel}>
              Form completion: {progress}%
            </Text>
            <Progress
              value={progress}
              color={progress === 100 ? "green" : "cyan"}
              size="sm"
              radius="xl"
              animated={submitting}
            />
          </Box>
        )}

        {/* Loading skeleton */}
        {externalLoading ? (
          <FormSkeleton fieldCount={Object.keys(schemaDefinition).length} />
        ) : (
          <form onSubmit={onSubmitForm(submit)}>
            <Stack gap={0}>
              {Object.entries(schemaDefinition).map(([key, value]) => (
                <Fragment key={key}>
                  <DynamicComponent
                    toRender={COMPONENTS[value.type]({
                      ...value,
                      id: key,
                      getInputProps,
                      errors,
                      formValues: undefined,
                    } as any)}
                  />
                  {(dynamicSubtexts[key] || value.subtext) && (
                    <Text className={classes.subtext}>
                      {dynamicSubtexts[key] || value.subtext}
                    </Text>
                  )}
                </Fragment>
              ))}

              <Button
                className={classes.submitButton}
                loading={submitting}
                type="submit"
                size={compact ? "sm" : "md"}
                fullWidth={compact}
              >
                {submitText || "Submit"}
              </Button>
            </Stack>
          </form>
        )}
      </Card>
    </Box>
  );
};

// Re-export types for external use
export type { Schema, InputConfig, SelectConfig, AutocompleteConfig };
